"use client";

import { PriceAnchor } from "@/components/services/price-anchor";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { m, useInView } from "framer-motion";
import {
  ShieldCheck,
  Activity,
  ServerCog,
  Calendar,
  Send,
  ArrowRight,
  Check,
  X,
  AlertTriangle,
  Lock,
  Database,
  Globe2,
  Cpu,
  Clock,
  HeartPulse,
  TrendingDown,
  Sparkles,
  Headphones,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────── */

/** Sanft animierte Zahl, die von 0 hochläuft sobald sichtbar. */
function CountUp({
  to,
  decimals = 0,
  duration = 1.4,
  suffix = "",
  prefix = "",
}: {
  to: number;
  decimals?: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(eased * to);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  const formatted = value.toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Hero (Status Dashboard Mockup)
 * ──────────────────────────────────────────────────────────── */

function StatusDashboard() {
  // Live ping/uptime numbers, leicht "wackelnd" – nur visuell
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1500);
    return () => clearInterval(id);
  }, []);

  const ping = 22 + ((tick * 7) % 11); // 22..32
  const cpu = 18 + ((tick * 3) % 9); // 18..26
  const apiMs = 84 + ((tick * 11) % 23); // 84..106

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 sm:p-6"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow-lg)",
      }}
    >
      {/* Soft brand-glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl" />

      {/* Header bar */}
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <p
            className="text-xs font-semibold"
            style={{ color: "var(--text)" }}
          >
            All systems operational
          </p>
        </div>
        <span
          className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
          style={{
            borderColor: "var(--border)",
            color: "var(--text-secondary)",
          }}
        >
          Frankfurt · DE
        </span>
      </div>

      {/* Big uptime metric */}
      <div className="relative mt-4 flex items-end justify-between gap-4">
        <div>
          <p
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--text-tertiary)" }}
          >
            Uptime · letzte 30 Tage
          </p>
          <p
            className="mt-1 text-4xl font-bold tracking-tight sm:text-5xl"
            style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
          >
            <CountUp to={99.98} decimals={2} suffix=" %" />
          </p>
        </div>
        <HeartPulseChart />
      </div>

      {/* Metric row */}
      <div className="relative mt-5 grid grid-cols-3 gap-2 sm:gap-3">
        {[
          {
            label: "Server CPU",
            value: `${cpu} %`,
            icon: Cpu,
          },
          {
            label: "API Latenz",
            value: `${apiMs} ms`,
            icon: Activity,
          },
          {
            label: "Ping",
            value: `${ping} ms`,
            icon: Globe2,
          },
        ].map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.label}
              className="rounded-xl border p-3"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <div className="flex items-center gap-1.5">
                <Icon
                  className="h-3 w-3"
                  style={{ color: "var(--text-tertiary)" }}
                />
                <p
                  className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {m.label}
                </p>
              </div>
              <p
                className="mt-1 text-base font-bold tabular-nums sm:text-lg"
                style={{ color: "var(--text)" }}
              >
                {m.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Service log */}
      <div className="relative mt-4 space-y-1.5">
        {[
          { time: "vor 2 min", text: "TLS-Zertifikat geprüft", ok: true },
          { time: "vor 8 min", text: "Backup erstellt (Snapshot)", ok: true },
          { time: "vor 14 min", text: "DSGVO-Audit Log rotiert", ok: true },
        ].map((l, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-lg px-3 py-2 text-xs"
            style={{
              backgroundColor: "var(--bg-secondary)",
              color: "var(--text-secondary)",
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/15"
              >
                <Check className="h-2.5 w-2.5 text-emerald-500" />
              </span>
              {l.text}
            </div>
            <span style={{ color: "var(--text-quaternary)" }}>{l.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Animierte EKG-artige Linie als optisches Pulse-Chart */
function HeartPulseChart() {
  return (
    <svg
      viewBox="0 0 200 56"
      className="h-12 w-32 sm:h-14 sm:w-40"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pulseGrad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="var(--color-brand-500)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--color-brand-500)" stopOpacity="1" />
          <stop offset="100%" stopColor="var(--color-brand-500)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <m.path
        d="M0 28 H30 L40 28 L48 8 L58 48 L68 18 L78 28 H120 L130 28 L140 14 L150 42 L160 22 L170 28 H200"
        fill="none"
        stroke="url(#pulseGrad)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity, repeatType: "loop" }}
      />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Downtime-Kalkulator
 * ──────────────────────────────────────────────────────────── */

function DowntimeCalculator() {
  const [revenue, setRevenue] = useState(8000); // monatlicher Umsatz €
  const [hours, setHours] = useState(6); // Ausfall in Stunden

  // Annahme: 30 Tage * 10h aktiver Verkauf = 300h, einfacher pro-rata-Wert
  const lossPerHour = revenue / 300;
  const totalLoss = Math.round(lossPerHour * hours);
  const seoRisk = Math.min(60, hours * 4); // grobe Heuristik

  return (
    <div
      className="rounded-2xl border p-5 lg:p-6"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow)",
      }}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: "var(--fi-error-bg)",
            color: "var(--fi-error-fg)",
          }}
        >
          Realität-Check
        </span>
      </div>
      <h3
        className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
        style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
      >
        Was kostet ein Ausfall – wirklich?
      </h3>
      <p
        className="mt-2 text-base leading-relaxed"
        style={{ color: "var(--text-tertiary)" }}
      >
        Schieben Sie die Regler auf Ihre Realität. Wir zeigen, was ein einziger
        Webseiten-Crash bei Ihnen gerade kostet – ohne Marketing-Bla.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Left: Sliders */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="revenue"
                className="text-sm font-semibold"
                style={{ color: "var(--text)" }}
              >
                Monatlicher Online-Umsatz
              </label>
              <span
                className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums"
                style={{
                  backgroundColor: "var(--fi-brand-bg)",
                  color: "var(--fi-brand-fg)",
                }}
              >
                {revenue.toLocaleString("de-DE")} €
              </span>
            </div>
            <input
              id="revenue"
              type="range"
              min={1000}
              max={50000}
              step={500}
              value={revenue}
              onChange={(e) => setRevenue(parseInt(e.target.value))}
              className="mt-2 w-full accent-brand-500"
            />
            <div
              className="mt-1 flex justify-between text-[10px]"
              style={{ color: "var(--text-quaternary)" }}
            >
              <span>1.000 €</span>
              <span>50.000 €</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="hours"
                className="text-sm font-semibold"
                style={{ color: "var(--text)" }}
              >
                Ausfalldauer
              </label>
              <span
                className="rounded-md px-2 py-0.5 text-sm font-bold tabular-nums"
                style={{
                  backgroundColor: "var(--fi-error-bg)",
                  color: "var(--fi-error-fg)",
                }}
              >
                {hours} h
              </span>
            </div>
            <input
              id="hours"
              type="range"
              min={1}
              max={48}
              step={1}
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value))}
              className="mt-2 w-full accent-brand-500"
            />
            <div
              className="mt-1 flex justify-between text-[10px]"
              style={{ color: "var(--text-quaternary)" }}
            >
              <span>1 h</span>
              <span>2 Tage</span>
            </div>
          </div>
        </div>

        {/* Right: Numbers */}
        <div
          className="flex flex-col justify-center gap-4 rounded-xl p-5"
          style={{ backgroundColor: "var(--bg-secondary)" }}
        >
          <div>
            <p
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "var(--text-tertiary)" }}
            >
              Geschätzter Umsatzverlust
            </p>
            <p
              key={totalLoss}
              className="mt-1 text-4xl font-bold tracking-tight tabular-nums"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              <m.span
                initial={{ opacity: 0.5, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                {totalLoss.toLocaleString("de-DE")} €
              </m.span>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-lg border p-3"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--card-bg)",
              }}
            >
              <p
                className="text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-tertiary)" }}
              >
                Verlorene Anfragen
              </p>
              <p
                className="mt-1 text-lg font-bold tabular-nums"
                style={{ color: "var(--text)" }}
              >
                ≈ {Math.round(hours * 1.5)}
              </p>
            </div>
            <div
              className="rounded-lg border p-3"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--card-bg)",
              }}
            >
              <p
                className="text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-tertiary)" }}
              >
                SEO-Schaden
              </p>
              <p
                className="mt-1 flex items-center gap-1 text-lg font-bold tabular-nums"
                style={{ color: "var(--text)" }}
              >
                <TrendingDown className="h-4 w-4 text-rose-500" />
                {seoRisk} %
              </p>
            </div>
          </div>
          <p
            className="text-xs leading-relaxed"
            style={{ color: "var(--text-tertiary)" }}
          >
            Werte sind Schätzungen auf Basis durchschnittlicher Konversionsraten und
            Suchmaschinen-Reaktionszeiten. Die reale Wirkung ist meist höher, weil
            Folgegeschäft, Vertrauensverlust und Crawl-Budget mitfallen.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Vergleich Mit / Ohne Wartung
 * ──────────────────────────────────────────────────────────── */

function ComparisonBlock() {
  const without = [
    "Sicherheits-Updates passieren irgendwann – oder nie",
    "Niemand merkt einen Ausfall, bis Kunden anrufen",
    "Backups gibt es theoretisch, getestet wurden sie nie",
    "Datenschutz-Gesetze ändern sich – Sie erfahren es zu spät",
    "Bei Problemen: lange Suche nach „dem Webentwickler von damals“",
  ];
  const withUs = [
    "Wir installieren Updates wöchentlich und prüfen die Auswirkungen",
    "24/7 Monitoring meldet Probleme bevor Kunden sie sehen",
    "Tägliche Backups, monatlich getesteter Restore",
    "DSGVO-Anpassungen automatisch eingespielt – mit Doku",
    "Ein fester Ansprechpartner, schnelle Reaktion, klare Wege",
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Ohne */}
      <div
        className="rounded-2xl border p-5 lg:p-6"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--card-bg)",
        }}
      >
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: "var(--fi-error-bg)",
            color: "var(--fi-error-fg)",
          }}
        >
          Ohne Wartungsservice
        </span>
        <h3
          className="mt-3 text-xl font-semibold"
          style={{ color: "var(--text)" }}
        >
          „Läuft schon irgendwie."
        </h3>
        <ul className="mt-4 space-y-2">
          {without.map((t) => (
            <li
              key={t}
              className="flex items-start gap-2.5 text-sm"
              style={{ color: "var(--text-secondary)" }}
            >
              <span
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: "var(--fi-error-bg)",
                }}
              >
                <X className="h-3 w-3" style={{ color: "var(--fi-error-fg)" }} />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>

      {/* Mit */}
      <div
        className="relative overflow-hidden rounded-2xl border p-5 lg:p-6"
        style={{
          borderColor: "var(--color-brand-500)",
          backgroundColor: "var(--card-bg)",
          boxShadow: "var(--card-shadow-lg)",
        }}
      >
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-400/15 blur-2xl" />
        <span
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: "var(--fi-brand-bg)",
            color: "var(--fi-brand-fg)",
          }}
        >
          <Sparkles className="h-3 w-3" />
          Mit DMD-Wartungsservice
        </span>
        <h3
          className="mt-3 text-xl font-semibold"
          style={{ color: "var(--text)" }}
        >
          „Ich muss nie mehr daran denken."
        </h3>
        <ul className="mt-4 space-y-2">
          {withUs.map((t) => (
            <li
              key={t}
              className="flex items-start gap-2.5 text-sm"
              style={{ color: "var(--text)" }}
            >
              <span
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: "var(--fi-success-bg)",
                }}
              >
                <Check
                  className="h-3 w-3"
                  style={{ color: "var(--fi-success-fg)" }}
                />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ──────────────────────────────────────────────────────────── */


/** Server-gerenderte Abschnitte des Seitenschemas (Auftrag 2026-10-28, Paket 7), vom page.tsx übergeben. */
export type ServiceSlots = {
  afterHero?: ReactNode;
  afterPrice?: ReactNode;
  beforeProcess?: ReactNode;
  beforeCta?: ReactNode;
  /** Hintergrund des CTA-Abschnitts, damit der Wechsel weiß/grau auch mit und ohne FAQ aufgeht (Paket 12, Nr. 8a). */
  ctaTone?: "primary" | "secondary";
};

export function WartungsserviceClient({ afterHero, afterPrice, beforeProcess, beforeCta, ctaTone = "secondary" }: ServiceSlots) {
  const pillars = useMemo(
    () => [
      {
        icon: ServerCog,
        kicker: "Cloud-Server",
        title: "Setup, Migration & Pflege – aus einer Hand",
        body: "Wir richten Ihren Cloud-Server ein, migrieren bestehende Projekte, halten Linux, Datenbank und Runtime aktuell und fahren saubere Backups. Serverstandort: Frankfurt am Main. Das macht Latenz spürbar niedrig und DSGVO-Auflagen sehr einfach.",
        bullets: [
          "Server-Provisioning & Härtung",
          "Tägliche Backups + monatlicher Restore-Test",
          "Datenbank-Wartung, Log-Rotation, TLS-Erneuerung",
        ],
      },
      {
        icon: Activity,
        kicker: "24/7 Monitoring",
        title: "Server, API & Ping – ohne blinde Flecken",
        body: "Drei Sensoren wachen rund um die Uhr: Server-Health (CPU, RAM, Disk), API-Endpoints (Antwortzeit, Fehler-Rate) und externer Ping (echte Erreichbarkeit). Auffälligkeiten landen auf unserem Pager, nicht auf Ihrem Schreibtisch.",
        bullets: [
          "Externer Uptime-Check alle 60 Sekunden",
          "Alerts an Bereitschaft – nachts, am Wochenende, an Feiertagen",
          "Monatlicher Status-Report mit Uptime-Quote",
        ],
      },
      {
        icon: ShieldCheck,
        kicker: "DSGVO-Compliance",
        title: "Bei Gesetzesänderungen aktualisieren wir – nicht Sie",
        body: "Cookie-Banner, Datenschutzhinweise, Auftragsverarbeitung, Logging-Pflichten: Wenn sich rechtliche Vorgaben ändern, passen wir Ihre Website nach – inkl. Doku. Sie bekommen einen Vermerk im Monatsbericht, kein neues Projekt auf den Tisch.",
        bullets: [
          "Anpassung an neue DSGVO/TTDSG-Urteile & Aufsichtshinweise",
          "Cookie-Versionierung mit revidierbarer Einwilligung",
          "Audit-Log-Aufbewahrung & Löschkonzept laufend gepflegt",
        ],
      },
    ],
    [],
  );

  return (
    <>
      {/* Breadcrumb */}
      <nav
        className="mx-auto max-w-7xl px-4 py-4 lg:px-8"
        aria-label="Breadcrumb"
      >
        <ol
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--text-tertiary)" }}
        >
          <li>
            <Link
              href="/"
              className="transition-colors hover:text-brand-500"
            >
              Startseite
            </Link>
          </li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li>
            <Link
              href="/dienstleistungen"
              className="transition-colors hover:text-brand-500"
            >
              Dienstleistungen
            </Link>
          </li>
          <li style={{ color: "var(--text-quaternary)" }}>/</li>
          <li className="font-medium" style={{ color: "var(--text)" }}>
            Wartungsservice
          </li>
        </ol>
      </nav>

      {/* Hero with split: text + dashboard */}
      <section className="pb-10 pt-4 lg:pb-12 lg:pt-8">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Left */}
            <div>
              <div
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--card-bg)",
                  color: "var(--text-secondary)",
                }}
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Exklusiv für Bestandskunden
              </div>

              <h1
                className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
                style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
              >
                Wartungsservice –{" "}
                <span className="text-brand-500">wir wachen, Sie schlafen.</span>
              </h1>

              <p
                className="mt-5 text-lg leading-relaxed"
                style={{ color: "var(--text-tertiary)" }}
              >
                Monatliches Abo für die Pflege Ihres Cloud-Servers in{" "}
                <strong style={{ color: "var(--text)" }}>Frankfurt</strong>. Wir
                übernehmen Einrichtung, Wartung, 24/7 Monitoring von Server, API
                und Ping – und halten alles bei Gesetzesänderungen
                DSGVO-konform.
              </p>

              {/* Pulse-Pillen */}
              <div
                className="mt-6 flex flex-wrap gap-2"
              >
                {[
                  { Icon: Globe2, text: "Frankfurt Cloud" },
                  { Icon: HeartPulse, text: "24/7 Monitoring" },
                  { Icon: Lock, text: "DSGVO laufend gepflegt" },
                ].map((p) => {
                  const Icon = p.Icon;
                  return (
                    <span
                      key={p.text}
                      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium"
                      style={{
                        borderColor: "var(--border)",
                        backgroundColor: "var(--card-bg)",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <Icon className="h-3.5 w-3.5 text-brand-500" />
                      {p.text}
                    </span>
                  );
                })}
              </div>

              <div
                className="mt-8 flex flex-col gap-3 sm:flex-row"
              >
                <Link
                  href="/beratung"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md"
                >
                  <Calendar className="h-5 w-5" />
                  Wartung freischalten
                </Link>
                <Link
                  href="/anfrage"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all duration-150 hover:shadow-sm"
                  style={{
                    color: "var(--text-secondary)",
                    borderColor: "var(--border-secondary)",
                  }}
                >
                  <Send className="h-4 w-4" />
                  Frage stellen
                </Link>
              </div>

              {/* Mini-trust row */}
              <div
                className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-xs"
                style={{ color: "var(--text-tertiary)" }}
              >
                <span className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-brand-500" />
                  Monatlich kündbar
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-brand-500" />
                  Server in Deutschland
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-brand-500" />
                  Ein Ansprechpartner
                </span>
              </div>
            </div>

            {/* Right: Dashboard */}
            <div
            >
              <StatusDashboard />
              <p
                className="mt-3 text-center text-[11px]"
                style={{ color: "var(--text-quaternary)" }}
              >
                Beispielhafte Visualisierung – das echte Status-Board sehen
                Bestandskunden im Dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {afterHero}

      {/* Preisanker – Texte aus src/lib/facts.ts (PRICE_ANCHORS), bewusst ohne Beträge (Entscheidung 2026-10-21) */}
      <PriceAnchor service="wartungsservice" />
      {afterPrice}

      {/* 3-Pillar grid */}
      <section
        className="py-12 lg:py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm font-semibold text-brand-500">
              Drei Säulen, ein Abo
            </p>
            <h2
              className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Was wir konkret übernehmen
            </h2>
            <p
              className="mt-3 text-base leading-relaxed"
              style={{ color: "var(--text-tertiary)" }}
            >
              Kein Stundenkonto, keine Bastellei – ein klar definierter Servicerahmen.
            </p>
          </m.div>

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <m.div
                  key={p.title}
                  className="group relative flex flex-col rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg lg:p-7"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: "var(--card-bg)",
                    boxShadow: "var(--card-shadow)",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl"
                    style={{
                      backgroundColor: "var(--fi-brand-bg)",
                      color: "var(--fi-brand-fg)",
                    }}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <p
                    className="mt-5 text-xs font-semibold uppercase tracking-wider text-brand-500"
                  >
                    {p.kicker}
                  </p>
                  <h3
                    className="mt-1 text-lg font-semibold"
                    style={{ color: "var(--text)" }}
                  >
                    {p.title}
                  </h3>
                  <p
                    className="mt-3 text-sm leading-relaxed"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {p.body}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {p.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-sm"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </m.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KPI Counter row */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div
            className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border lg:grid-cols-4"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--border)",
            }}
          >
            {[
              {
                label: "Server-Standort",
                value: "Frankfurt",
                hint: "DSGVO-Zone EU",
                Icon: Globe2,
              },
              {
                label: "Monitoring",
                value: "24/7",
                hint: "365 Tage / Jahr",
                Icon: Headphones,
              },
              {
                label: "Uptime-Ziel",
                value: "99,9 %",
                hint: "+ Restore-Plan",
                Icon: Activity,
              },
              {
                label: "Backup-Frequenz",
                value: "täglich",
                hint: "30 Tage Aufbewahrung",
                Icon: Database,
              },
            ].map((k) => {
              const Icon = k.Icon;
              return (
                <div
                  key={k.label}
                  className="p-6"
                  style={{ backgroundColor: "var(--card-bg)" }}
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                    <p
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {k.label}
                    </p>
                  </div>
                  <p
                    className="mt-2 text-2xl font-bold tracking-tight"
                    style={{ color: "var(--text)" }}
                  >
                    {k.value}
                  </p>
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--text-quaternary)" }}
                  >
                    {k.hint}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Downtime calculator */}
      <section
        className="py-12 lg:py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <DowntimeCalculator />
        </div>
      </section>

      {/* Comparison */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm font-semibold text-brand-500">
              Vorher / Nachher
            </p>
            <h2
              className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Der Unterschied liegt in der Ruhe.
            </h2>
            <p
              className="mt-3 text-base leading-relaxed"
              style={{ color: "var(--text-tertiary)" }}
            >
              Beide Spalten beschreiben den gleichen Tag – nur fühlt sich der
              eine wie Brandschutz, der andere wie Brand an.
            </p>
          </m.div>
          <div className="mt-8">
            <ComparisonBlock />
          </div>
        </div>
      </section>

      {/* Eligibility / Scarcity */}
      <section
        className="py-12 lg:py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <m.div
            className="relative overflow-hidden rounded-2xl border p-5 lg:p-6"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--card-bg)",
              boxShadow: "var(--card-shadow)",
            }}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-400/10 blur-3xl" />
            <div className="flex items-start gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                style={{
                  backgroundColor: "var(--fi-brand-bg)",
                  color: "var(--fi-brand-fg)",
                }}
              >
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                  Wer kann buchen?
                </p>
                <h3
                  className="mt-1 text-xl font-semibold"
                  style={{ color: "var(--text)" }}
                >
                  Nur für Kunden, deren Website wir gebaut haben.
                </h3>
                <p
                  className="mt-3 text-base leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Klingt streng – ist es auch. Wir können nur dann ehrlich für
                  Stabilität, Sicherheit und DSGVO-Konformität geradestehen,
                  wenn wir den Code, die Infrastruktur und die Konfiguration
                  selbst verantworten. Fremde Codebases halten wir nicht – das
                  wäre ein Versprechen, das wir nachts nicht halten könnten.
                </p>
                <div
                  className="mt-4 flex items-start gap-2 rounded-lg p-3 text-sm"
                  style={{
                    backgroundColor: "var(--fi-warning-bg)",
                    color: "var(--fi-warning-fg)",
                  }}
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Sie haben Ihre aktuelle Website nicht von uns? Kein Problem
                    – wir können einen{" "}
                    <Link
                      href="/dienstleistungen/webdesign"
                      className="font-semibold underline-offset-2 hover:underline"
                    >
                      Relaunch
                    </Link>{" "}
                    planen und Ihren Wartungsservice direkt im Anschluss
                    aktivieren.
                  </p>
                </div>
              </div>
            </div>
          </m.div>
        </div>
      </section>

      {beforeProcess}

      {/* Process (grau) */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm font-semibold text-brand-500">So läuft’s ab</p>
            <h2
              className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Vom Klick zur ruhigen Nacht.
            </h2>
          </m.div>

          <ol className="mt-8 space-y-3">
            {[
              {
                step: "Bestandscheck",
                what: "Wir prüfen Ihre aktuelle Server-Konfiguration, Backups, TLS, DSGVO-Stack. Bei Lücken: kurzer Plan, dann Umzug auf unseren Frankfurt-Stack.",
                time: "1–2 Werktage",
              },
              {
                step: "Aktivierung Monitoring",
                what: "Server-, API- und Ping-Sensoren werden eingerichtet, Alerts auf unser Bereitschafts-Team. Sie sehen Ihren Status live im Dashboard.",
                time: "1 Werktag",
              },
              {
                step: "Laufender Betrieb",
                what: "Updates wöchentlich, Backups täglich, DSGVO-Anpassungen anlassbezogen. Bei Vorfällen reagieren wir – auch nachts und am Wochenende.",
                time: "monatlich",
              },
              {
                step: "Monats-Report",
                what: "Sie erhalten eine kurze, lesbare Zusammenfassung: Uptime, gemachte Updates, anstehende DSGVO-Themen, Empfehlungen.",
                time: "jeden 1. des Monats",
              },
            ].map((p, i) => (
              <m.li
                key={p.step}
                className="flex gap-4 rounded-xl border p-4"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--card-bg)",
                }}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                  style={{
                    backgroundColor: "var(--fi-brand-bg)",
                    color: "var(--fi-brand-fg)",
                  }}
                >
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p
                      className="text-base font-semibold"
                      style={{ color: "var(--text)" }}
                    >
                      {p.step}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 text-xs"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      <Clock className="h-3 w-3" />
                      {p.time}
                    </span>
                  </div>
                  <p
                    className="mt-1 text-sm leading-relaxed"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {p.what}
                  </p>
                </div>
              </m.li>
            ))}
          </ol>
        </div>
      </section>

      {beforeCta}

      {/* Final CTA */}
      <section
        className="py-12 lg:py-16"
        style={ctaTone === "secondary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}
      >
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
          >
            <h2
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Lassen Sie uns das Wachen übernehmen.
            </h2>
            <p
              className="mt-3 text-base leading-relaxed"
              style={{ color: "var(--text-tertiary)" }}
            >
              Im 30-Minuten-Erstgespräch klären wir, in welcher Stufe Ihre
              Website betreut werden sollte – und Sie bekommen ein konkretes
              Monatspaket. Keine Vertragslaufzeit, monatlich kündbar.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                href="/beratung"
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md"
              >
                <Calendar className="h-5 w-5" />
                Kostenloses Erstgespräch
              </Link>
              <Link
                href="/anfrage"
                className="inline-flex items-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all duration-150 hover:shadow-sm"
                style={{
                  color: "var(--text-secondary)",
                  borderColor: "var(--border-secondary)",
                }}
              >
                Anfrage senden
              </Link>
            </div>
          </m.div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 lg:py-16" style={ctaTone === "primary" ? { backgroundColor: "var(--bg-secondary)" } : undefined}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2
            className="text-center text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            Das könnte Sie auch interessieren
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {[
              { name: "Webdesign – die Basis", href: "/dienstleistungen/webdesign" },
              { name: "SEO – nachhaltig sichtbar", href: "/dienstleistungen/seo" },
              {
                name: "Software-Entwicklung",
                href: "/dienstleistungen/software-entwicklung",
              },
            ].map((s) => (
              <Link
                key={s.href}
                href={s.href}
                className="group inline-flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-medium transition-all duration-150 hover:shadow-sm"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text-secondary)",
                }}
              >
                {s.name}
                <ArrowRight className="h-3.5 w-3.5 text-brand-500 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
